import React, { useEffect, useContext, useRef } from "react";
import PropTypes from "prop-types";

import { productUrl } from "views_shared/app/javascript/utils/urls";
import ProfilePage from "../../profiles/ProfilePage";
import ProductHeader from "./product_header";
import ProductSections from "./product_sections";
import ProductSidebar from "./sidebar";
import ProductShowSkeleton from "./product_show_skeleton";
import EditModal from "../edit/edit_modal";
import { ProductContext } from "../data/product_context_management";

// Custom hook to get the previous value
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function ProductShow({ productId }) {
  const {
    setProductId,
    asyncError,
    checklistContributes,
    product,
    topLevelIsLoading,
    showConnectCard,
    showCompanyCard,
    showRepresentedByCard,
    showSamplesCard,
  } = useContext(ProductContext);

  const currentSlug = product.slug;
  const previousSlug = usePrevious(currentSlug);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  useEffect(() => {
    // V1-COMPAT: on V1 the equivalent page is the id-based /products/:id (see productUrl in
    // views_shared/utils/urls), which a slug change never affects, so there is nothing to redirect.
    if (globalThis.Qwoted?.V1_HOSTED) return;
    if (previousSlug && currentSlug && previousSlug !== currentSlug) {
      window.location.href = productUrl({ slug: currentSlug, migrate_id: product.migrate_id });
    }
  }, [previousSlug, currentSlug, product.migrate_id]);

  useEffect(() => {
    setProductId(productId);
  }, [productId, setProductId]);

  // Whether the rail column gets reserved at all, decided BEFORE the slot is passed (ProfilePage
  // reserves the column for any non-null rail). The rail collapses only for a viewer who gets none
  // of its cards — anonymous, no contact details, no company, no representatives, no samples, no
  // checklist. While the first read is in flight we reserve it: the loaded page almost always has a
  // rail, and reserving then collapsing beats snapping from full-width to two-column on load.
  const railHasContent =
    topLevelIsLoading ||
    checklistContributes ||
    showConnectCard ||
    showCompanyCard ||
    showRepresentedByCard ||
    showSamplesCard;

  return (
    <div>
      <EditModal />

      <ProfilePage
        isLoading={topLevelIsLoading}
        skeleton={<ProductShowSkeleton railHasContent={railHasContent} />}
        header={<ProductHeader />}
        rightRail={railHasContent ? <ProductSidebar /> : null}
      >
        <ProductSections />
      </ProfilePage>
    </div>
  );
}

ProductShow.propTypes = {
  productId: PropTypes.string.isRequired,
};

export default ProductShow;
